import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { useList, useUsersMap, useInvalidate } from "@/lib/hooks";
import api, { apiError } from "@/lib/api";
import { Panel, StatusBadge, Field, money } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ArrowLeft, Phone, Envelope, MapPin, IdentificationCard, Buildings, Package, Truck, Target,
  UserCircle, CalendarBlank, MagnifyingGlass, Prohibit, PencilSimple, FloppyDisk, Trash,
  ClockCounterClockwise, ChatText, CheckCircle, ArrowBendUpLeft, Note, PhoneCall, Lightning,
  UploadSimple, FileArrowDown, FolderOpen, Warning, Camera, PaperPlaneTilt,
} from "@phosphor-icons/react";
import { ProposalDialog } from "@/components/ProposalDialog";
import { toast } from "sonner";
import { LEAD_STATUSES, LEAD_PIPELINE, STATUS_SHORT, REJECTED_STATUSES, CARRIER_READY_STATUSES, SUITABLE_PLANS, TRUCK_TYPES, AUTO_STAGES, leadStuckDays, SLA_DAYS } from "@/lib/leadConfig";
import { LeadStatusPill, OnboardingTypeChip, RiskRejectDialog } from "@/components/LeadStatus";
import { uploadAsset, mediaUrl } from "@/lib/upload";

export function LeadDocuments({ leadId }) {
  const { data: docs = [], refetch } = useList(["lead-docs", leadId], `/leads/${leadId}/documents`);
  const fileRef = React.useRef(null);
  const [uploading, setUploading] = useState(false);
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadAsset(file, "lead-docs");
      await api.post(`/leads/${leadId}/documents`, { url: res.url, filename: res.filename, content_type: res.content_type, doc_type: "general" });
      refetch(); toast.success("Document uploaded");
    } catch (err) { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const remove = async (id) => { await api.delete(`/lead-documents/${id}`); refetch(); };
  return (
    <div data-testid="lead-documents">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">Rate confirmations, authority, insurance and any lead paperwork.</p>
        <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold" data-testid="lead-doc-upload-btn"><UploadSimple size={16} className="mr-1.5" />{uploading ? "Uploading…" : "Upload"}</Button>
        <input ref={fileRef} type="file" className="hidden" onChange={onPick} data-testid="lead-doc-input" />
      </div>
      {docs.length === 0 ? <p className="text-sm text-slate-400">No documents yet.</p> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {docs.map((d) => {
            const img = (d.content_type || "").startsWith("image/");
            return (
              <div key={d.id} className="flex items-center gap-3 border border-slate-200 rounded-lg p-3" data-testid={`lead-doc-${d.id}`}>
                {img ? <img src={mediaUrl(d.url)} alt="" className="w-12 h-12 rounded object-cover" /> : <div className="w-12 h-12 rounded bg-blue-50 grid place-items-center"><FileArrowDown size={20} className="text-blue-600" /></div>}
                <div className="min-w-0 flex-1"><p className="text-sm font-medium text-slate-800 truncate">{d.filename || "Document"}</p><p className="text-xs text-slate-400">{d.uploaded_by_name} · {new Date(d.created_at).toLocaleDateString()}</p></div>
                <a href={mediaUrl(d.url)} target="_blank" rel="noreferrer" className="p-1.5 text-slate-400 hover:text-blue-600"><FileArrowDown size={16} /></a>
                <button onClick={() => remove(d.id)} className="p-1.5 text-slate-400 hover:text-red-500" data-testid={`lead-doc-del-${d.id}`}><Trash size={16} /></button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "—");
const fmtTime = (d) => new Date(d).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

function Gauge({ value = 0 }) {
  const v = Math.max(0, Math.min(100, value));
  const color = v >= 66 ? "#10b981" : v >= 33 ? "#f59e0b" : "#ef4444";
  return (
    <div className="relative w-20 h-20 shrink-0" style={{ background: `conic-gradient(${color} ${v * 3.6}deg, #e2e8f0 0deg)`, borderRadius: "9999px" }}>
      <div className="absolute inset-[6px] rounded-full bg-white grid place-items-center">
        <span className="text-lg font-heading font-extrabold text-slate-900 tabular">{v}%</span>
      </div>
    </div>
  );
}

function StageBar({ lead, canEdit, onSet }) {
  const rejected = REJECTED_STATUSES.includes(lead.status);
  const curIdx = LEAD_PIPELINE.findIndex((s) => s.key === lead.status);
  return (
    <div>
      <div className="flex overflow-x-auto no-scrollbar rounded-lg" data-testid="lead-stage-bar">
        {LEAD_PIPELINE.map((s, i) => {
          const done = curIdx >= 0 && i < curIdx;
          const active = curIdx === i;
          const bg = active ? s.color : done ? "#0f172a" : "#e2e8f0";
          const fg = active || done ? "#fff" : "#64748b";
          const first = i === 0;
          return (
            <button
              key={s.key}
              disabled={!canEdit}
              onClick={() => canEdit && onSet(s.key)}
              data-testid={`stage-${i}`}
              className={`relative flex-1 min-w-[120px] text-xs font-semibold py-2.5 pl-5 pr-3 transition-colors ${canEdit ? "cursor-pointer hover:opacity-90" : "cursor-default"}`}
              style={{
                background: bg, color: fg, marginLeft: first ? 0 : -14,
                clipPath: first
                  ? "polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%)"
                  : "polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%, 14px 50%)",
              }}
            >
              {s.short}
            </button>
          );
        })}
      </div>
      {rejected && (
        <div className="mt-2 flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700" data-testid="lead-rejected-banner">
          <Prohibit size={16} weight="fill" /><span className="font-semibold">{lead.status}</span>
          {lead.rejection_reason && <span className="text-red-600">· {lead.rejection_reason}</span>}
        </div>
      )}
    </div>
  );
}

const ACT_ICON = { note: Note, status: Lightning, created: CheckCircle, call: PhoneCall, converted: CheckCircle };

function Timeline({ leadId }) {
  const { data: acts = [], refetch } = useList(["lead-activities", leadId], `/leads/${leadId}/activities`);
  const [note, setNote] = useState("");
  const [type, setType] = useState("note");
  const add = async () => {
    if (!note.trim()) return;
    await api.post(`/leads/${leadId}/activities`, { type, text: note });
    setNote(""); refetch();
  };
  return (
    <div data-testid="lead-timeline">
      <div className="flex gap-2 mb-4">
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-28 bg-white border-slate-300 shrink-0" data-testid="activity-type"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="note">Note</SelectItem><SelectItem value="call">Call</SelectItem><SelectItem value="email">Email</SelectItem></SelectContent>
        </Select>
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Log a call, note or next step…" className="bg-white border-slate-300" data-testid="lead-note-input" onKeyDown={(e) => e.key === "Enter" && add()} />
        <Button onClick={add} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold shrink-0" data-testid="lead-note-add">Log</Button>
      </div>
      {acts.length === 0 ? <p className="text-sm text-slate-400">No activity yet. Logged calls and status changes appear here.</p> : (
        <div className="relative pl-6">
          <div className="absolute left-[9px] top-1 bottom-1 w-px bg-slate-200" />
          <div className="space-y-5">
            {acts.map((a) => {
              const Icon = ACT_ICON[a.type] || ChatText;
              return (
                <div key={a.id} className="relative">
                  <div className="absolute -left-6 top-0 w-[19px] h-[19px] rounded-full bg-white border-2 border-blue-500 grid place-items-center">
                    <Icon size={10} weight="fill" className="text-blue-600" />
                  </div>
                  <p className="text-sm text-slate-800">{a.text}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{a.user_name} · {fmtTime(a.created_at)}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2.5 py-2 border-b border-slate-100 last:border-0">
      <Icon size={15} className="text-slate-400 mt-0.5 shrink-0" weight="duotone" />
      <div className="min-w-0 flex-1"><p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</p><p className="text-sm text-slate-800 break-words">{value || "—"}</p></div>
    </div>
  );
}

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSales = user.role === "sales";
  const isAdmin = user.role === "admin";
  const [proposalOpen, setProposalOpen] = useState(false);
  const invalidate = useInvalidate();
  const { users } = useUsersMap();
  const salesAgents = users.filter((u) => u.role === "sales" || u.role === "admin");
  const nameOf = (uid) => users.find((u) => u.id === uid)?.name || "Unassigned";

  const { data: lead, isLoading, refetch } = useQuery({
    queryKey: ["lead", id],
    queryFn: async () => (await api.get(`/leads/${id}`)).data,
  });

  const [editMode, setEditMode] = useState(false);

  const [riskOpen, setRiskOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const leadPhotoRef = React.useRef(null);
  const [photoUploading, setPhotoUploading] = useState(false);

  const onLeadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoUploading(true);
    try {
      const res = await uploadAsset(file, "lead-avatars");
      await api.put(`/leads/${id}`, { avatar_url: res.url });
      refetch(); toast.success("Lead photo updated");
    } catch (err) { toast.error("Photo upload failed"); }
    finally { setPhotoUploading(false); if (leadPhotoRef.current) leadPhotoRef.current.value = ""; }
  };

  React.useEffect(() => { if (lead) setForm(lead); }, [lead]);

  if (isLoading || !lead) return <div className="p-8 text-slate-400">Loading lead…</div>;

  const canEditStage = isAdmin || isSales;
  const canReject = isSales && lead.status === "SD Team - Assigned by Compliance";
  const canConvert = isAdmin && CARRIER_READY_STATUSES.includes(lead.status) && !lead.carrier_id;
  const inp = "bg-white border-slate-300";

  const setStatus = async (status, extra = {}) => {
    if (status === "Risk Rejected" && !extra.risk_types) { setRiskOpen(true); return; }
    try { await api.put(`/leads/${id}`, { status, ...extra }); toast.success(`Stage → ${STATUS_SHORT[status] || status}`); refetch(); invalidate(["leads", "dashboard", "leads-funnel"]); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const salesReject = async () => {
    const reason = window.prompt("Reason for rejecting this lead?");
    if (reason === null) return;
    try { await api.put(`/leads/${id}`, { status: "Rejected - SD Team", rejection_reason: reason || "" }); toast.success("Lead rejected and sent back"); refetch(); invalidate(["leads", "dashboard", "leads-funnel"]); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const convertToCarrier = async () => {
    if (!window.confirm(`Convert "${lead.company}" into an Active Carrier?`)) return;
    try { await api.post(`/leads/${id}/convert-to-carrier`, {}); toast.success("Lead converted to Active Carrier"); refetch(); invalidate(["leads", "carriers", "leads-funnel"]); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, estimated_value: Number(form.estimated_value) || 0, num_trucks: Number(form.num_trucks) || 0, conversion_possibility: Number(form.conversion_possibility) || 0, assigned_to: form.assigned_to || null };
      await api.put(`/leads/${id}`, payload);
      toast.success("Lead updated"); setEditMode(false); refetch(); invalidate(["leads", "leads-funnel"]);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this lead permanently?")) return;
    await api.delete(`/leads/${id}`); invalidate(["leads", "dashboard", "leads-funnel"]); navigate("/leads");
  };

  return (
    <div className="pb-8">
      {/* Top bar */}
      <button onClick={() => navigate("/leads")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-blue-600 mb-3 font-medium" data-testid="lead-back"><ArrowLeft size={16} />Lead Management</button>

      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="relative shrink-0">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-blue-400 grid place-items-center text-white font-heading font-extrabold text-xl overflow-hidden">
              {lead.avatar_url ? <img src={mediaUrl(lead.avatar_url)} alt="" className="w-full h-full object-cover" data-testid="lead-avatar-img" /> : (lead.company?.[0]?.toUpperCase() || "?")}
            </div>
            <button onClick={() => leadPhotoRef.current?.click()} disabled={photoUploading} className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-blue-600 text-white grid place-items-center shadow ring-2 ring-white hover:bg-blue-700" title="Add lead photo" data-testid="lead-photo-btn"><Camera size={12} weight="fill" /></button>
            <input ref={leadPhotoRef} type="file" accept="image/*" className="hidden" onChange={onLeadPhoto} data-testid="lead-photo-input" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-heading font-extrabold text-2xl text-slate-900 leading-tight">{lead.company}</h1>
              <span className="font-mono text-sm text-blue-600 font-semibold">#{lead.lead_number}</span>
              {lead.carrier_id && <span className="text-[11px] font-bold uppercase tracking-wide text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">Active Carrier</span>}
              <OnboardingTypeChip type={lead.onboarding_type} />
            </div>
            <p className="text-sm text-slate-500">{lead.owner_manager || lead.contact_name || "No contact"} · {lead.phone || "no phone"}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={() => setProposalOpen(true)} className="bg-slate-900 text-white hover:bg-slate-800 font-semibold" data-testid="send-proposal-btn"><PaperPlaneTilt size={15} className="mr-1" />Send Company Proposal</Button>
          {canReject && <Button onClick={salesReject} className="bg-red-500 text-white hover:bg-red-600 font-semibold" data-testid="sales-reject-btn"><Prohibit size={15} className="mr-1" />Reject Lead</Button>}
          {canConvert && <Button onClick={convertToCarrier} className="bg-emerald-600 text-white hover:bg-emerald-700 font-semibold" data-testid="convert-carrier-btn"><Lightning size={15} weight="fill" className="mr-1" />Convert to Carrier</Button>}
          {canEditStage && (
            <Select value={lead.status} onValueChange={(v) => setStatus(v)}>
              <SelectTrigger className="w-56 bg-white border-slate-300" data-testid="lead-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s} disabled={AUTO_STAGES.includes(s) && !isAdmin}>{s}{AUTO_STAGES.includes(s) ? " (Auto)" : ""}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {isAdmin && !editMode && <Button variant="outline" onClick={() => setEditMode(true)} className="border-slate-300 bg-white text-slate-700" data-testid="edit-details-btn"><PencilSimple size={15} className="mr-1" />Edit</Button>}
          {isAdmin && <button onClick={remove} className="p-2 text-slate-400 hover:text-red-500" data-testid="delete-lead-btn"><Trash size={17} /></button>}
        </div>
      </div>

      {/* Stage bar */}
      <StageBar lead={lead} canEdit={canEditStage} onSet={(s) => (AUTO_STAGES.includes(s) && !isAdmin ? toast.info("OPI stages update automatically from the carrier packet") : setStatus(s))} />
      <RiskRejectDialog lead={riskOpen ? lead : null} onClose={() => setRiskOpen(false)} onSubmit={(body) => setStatus(body.status, body)} />
      {lead.status === "Risk Rejected" && (lead.risk_types || []).length > 0 && (
        <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="lead-risk-info"><b>Risks observed:</b> {lead.risk_types.join(", ")}{lead.rejection_reason ? ` · ${lead.rejection_reason}` : ""}{lead.risk_rejected_by_name ? ` · by ${lead.risk_rejected_by_name}` : ""}</div>
      )}
      {lead.authorized_by_name && (
        <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 flex flex-wrap gap-x-4 gap-y-1" data-testid="lead-approval-info">
          <b>Approved by {lead.authorized_by_name}</b><span>{(lead.authorized_at || "").slice(0, 10)}</span><span>Risk Check: {lead.risk_check || "—"}</span><span>Onboarding: {lead.onboarding_completed ? "Completed" : "—"}</span><span>Auto Debit: {lead.auto_debit_enabled ? "Enabled" : "Not Enabled"}</span>{lead.authorization_notes && <span>“{lead.authorization_notes}”</span>}
        </div>
      )}
      {leadStuckDays(lead) >= SLA_DAYS && (
        <div className="mt-2 flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700" data-testid="lead-stuck-flag">
          <Warning size={16} weight="fill" /><span className="font-semibold">Stuck {leadStuckDays(lead)} days</span><span className="text-red-600">· no stage movement past the {SLA_DAYS}-day SLA. Follow up or advance the stage.</span>
        </div>
      )}

      {/* Body */}
      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-5 mt-5">
        {/* Left profile column */}
        <div className="space-y-4">
          <Panel className="p-5">
            <div className="flex items-center gap-4 mb-4">
              <Gauge value={lead.conversion_possibility || 0} />
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Conversion</p>
                <p className="text-sm text-slate-600">Likelihood to close</p>
                <LeadStatusPill status={lead.status} className="mt-1.5" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-100">
              <div><p className="text-[11px] uppercase text-slate-400 font-semibold">Plan</p><p className="text-sm text-slate-800">{lead.suitable_plan || "—"}</p></div>
              {isAdmin && <div><p className="text-[11px] uppercase text-slate-400 font-semibold">Est. Value</p><p className="text-sm font-mono text-slate-800">{money(lead.estimated_value)}</p></div>}
              <div><p className="text-[11px] uppercase text-slate-400 font-semibold">Trucks</p><p className="text-sm text-slate-800">{lead.num_trucks || "—"}</p></div>
              <div><p className="text-[11px] uppercase text-slate-400 font-semibold">Add-on</p><p className="text-sm text-slate-800">{lead.additional_package ? "Yes" : "No"}</p></div>
            </div>
          </Panel>

          <Panel className="p-5">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-1">Contact & Authority</p>
            <Row icon={UserCircle} label="Owner / Manager" value={lead.owner_manager} />
            <Row icon={Phone} label="Phone" value={lead.phone} />
            <Row icon={Envelope} label="Email" value={lead.email} />
            <Row icon={MapPin} label="Address" value={lead.address} />
            <Row icon={IdentificationCard} label="MC Number" value={lead.mc_number} />
            <Row icon={IdentificationCard} label="USDOT" value={lead.usdot} />
          </Panel>

          <Panel className="p-5">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-1">Ownership</p>
            <Row icon={UserCircle} label="Assigned Agent" value={nameOf(lead.assigned_to)} />
            <Row icon={MagnifyingGlass} label="Source" value={lead.source} />
            <Row icon={CalendarBlank} label="Lead Born Date" value={fmtDate(lead.created_at)} />
            <Row icon={Truck} label="Truck Types" value={(lead.truck_types || []).join(", ")} />
          </Panel>
        </div>

        {/* Main column */}
        <Panel className="p-0 overflow-hidden">
          <Tabs defaultValue="activity">
            <div className="px-5 pt-4 border-b border-slate-200">
              <TabsList className="bg-transparent p-0 h-auto gap-4">
                <TabsTrigger value="activity" className="data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 border-b-2 border-transparent rounded-none pb-3 px-0 text-slate-500 font-semibold" data-testid="tab-activity"><ClockCounterClockwise size={16} className="mr-1.5" />Activity</TabsTrigger>
                <TabsTrigger value="details" className="data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 border-b-2 border-transparent rounded-none pb-3 px-0 text-slate-500 font-semibold" data-testid="tab-details"><Buildings size={16} className="mr-1.5" />Details</TabsTrigger>
                <TabsTrigger value="documents" className="data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 border-b-2 border-transparent rounded-none pb-3 px-0 text-slate-500 font-semibold" data-testid="tab-documents"><FolderOpen size={16} className="mr-1.5" />Documents</TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="activity" className="p-5 mt-0">
              <Timeline leadId={id} />
            </TabsContent>

            <TabsContent value="documents" className="p-5 mt-0">
              <LeadDocuments leadId={id} />
            </TabsContent>

            <TabsContent value="details" className="p-5 mt-0">
              {editMode && form ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Carrier Company *"><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={inp} data-testid="edit-company" /></Field>
                  <Field label="Owner / Manager"><Input value={form.owner_manager || ""} onChange={(e) => setForm({ ...form, owner_manager: e.target.value })} className={inp} /></Field>
                  <Field label="Email"><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inp} /></Field>
                  <Field label="Phone"><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inp} /></Field>
                  <Field label="MC #"><Input value={form.mc_number || ""} onChange={(e) => setForm({ ...form, mc_number: e.target.value })} className={`${inp} font-mono`} /></Field>
                  <Field label="USDOT #"><Input value={form.usdot || ""} onChange={(e) => setForm({ ...form, usdot: e.target.value })} className={`${inp} font-mono`} /></Field>
                  <div className="sm:col-span-2"><Field label="Address"><Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inp} /></Field></div>
                  <Field label="No. of Trucks"><Input type="number" value={form.num_trucks || 0} onChange={(e) => setForm({ ...form, num_trucks: e.target.value })} className={`${inp} font-mono`} /></Field>
                  <Field label="Estimated Value ($)"><Input type="number" value={form.estimated_value || 0} onChange={(e) => setForm({ ...form, estimated_value: e.target.value })} className={`${inp} font-mono`} /></Field>
                  <Field label="Conversion %"><Input type="number" value={form.conversion_possibility || 0} onChange={(e) => setForm({ ...form, conversion_possibility: e.target.value })} className={`${inp} font-mono`} /></Field>
                  <Field label="Suitable Plan">
                    <Select value={form.suitable_plan || "none"} onValueChange={(v) => setForm({ ...form, suitable_plan: v === "none" ? "" : v })}>
                      <SelectTrigger className={inp}><SelectValue placeholder="Select plan" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">—</SelectItem>{SUITABLE_PLANS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Assign to Agent">
                    <Select value={form.assigned_to || "none"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "none" ? "" : v })}>
                      <SelectTrigger className={inp}><SelectValue placeholder="Unassigned" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">Unassigned</SelectItem>{salesAgents.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Truck Types">
                      <div className="flex flex-wrap gap-2">
                        {TRUCK_TYPES.map((t) => {
                          const on = (form.truck_types || []).includes(t);
                          return <button type="button" key={t} onClick={() => setForm({ ...form, truck_types: on ? form.truck_types.filter((x) => x !== t) : [...(form.truck_types || []), t] })} className={`px-2.5 py-1 rounded-full text-sm font-medium border transition-colors ${on ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-300 hover:border-blue-400"}`}>{t}</button>;
                        })}
                      </div>
                    </Field>
                  </div>
                  <div className="sm:col-span-2"><Field label="Notes"><Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inp} rows={3} /></Field></div>
                  <div className="sm:col-span-2 flex justify-end gap-2">
                    <Button variant="outline" onClick={() => { setEditMode(false); setForm(lead); }} className="border-slate-300 bg-white text-slate-700">Cancel</Button>
                    <Button onClick={save} disabled={saving} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold" data-testid="save-edit-btn"><FloppyDisk size={16} className="mr-1.5" />{saving ? "Saving…" : "Save Changes"}</Button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1">
                  <Row icon={Buildings} label="Carrier Company" value={lead.company} />
                  <Row icon={Package} label="Suitable Plan" value={lead.suitable_plan} />
                  <Row icon={Target} label="Carrier Requirement" value={lead.carrier_requirement} />
                  <Row icon={Truck} label="Expected Gross / Week" value={lead.expected_gross_weekly ? money(lead.expected_gross_weekly) : "—"} />
                  <Row icon={Package} label="Additional Package" value={lead.additional_package ? "Yes" : "No"} />
                  <Row icon={Target} label="Conversion Possibility" value={`${lead.conversion_possibility || 0}%`} />
                  {lead.notes && <div className="sm:col-span-2 pt-3"><p className="text-[11px] uppercase text-slate-400 font-semibold mb-1">Notes</p><p className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3">{lead.notes}</p></div>}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </Panel>
      </div>
      {lead && <ProposalDialog lead={lead} open={proposalOpen} onOpenChange={setProposalOpen} onSent={() => { refetch(); invalidate(["lead-activities"]); }} />}
    </div>
  );
}
